import { Tag } from 'antd'
import React, { useMemo } from 'react'
import {
  formatCatchBoardActivitySummary,
  getCatchBoardActivityTypeLabel,
} from './catchBoardConstants'
import {
  formatCatchBoardDate,
  formatCatchBoardRelativeTime,
} from 'src/utils/catchBoardMappers'
import styled from 'styled-components/macro'

/**
 * Read-only Catch Board activity log — newest first.
 */
const CatchBoardDealActivityTimeline = ({ activityLog = [] }) => {
  const entries = useMemo(() => {
    return [...(activityLog || [])].sort((a, b) => {
      const aAt = a?.at ? new Date(a.at).getTime() : 0
      const bAt = b?.at ? new Date(b.at).getTime() : 0
      return bAt - aAt
    })
  }, [activityLog])

  return (
    <Section data-testid='catch-board-deal-activity'>
      <SectionTitle>Activity</SectionTitle>
      <SectionHint data-testid='catch-board-deal-activity-hint'>
        Audit trail for this deal — stage moves, readiness, notes, venues, and
        value changes.
      </SectionHint>

      {!entries.length ? (
        <EmptyHint data-testid='catch-board-deal-activity-empty'>
          No activity recorded yet.
        </EmptyHint>
      ) : (
        <Timeline data-testid='catch-board-deal-activity-list'>
          {entries.map((entry, index) => {
            const summary = formatCatchBoardActivitySummary(entry)
            return (
              <Item
                key={entry.activityId || `${entry.type}-${entry.at}-${index}`}
                data-testid={`catch-board-deal-activity-item-${entry.type}`}
                data-activity-type={entry.type || ''}
              >
                <Dot />
                <ItemBody>
                  <ItemHeader>
                    <Tag>{getCatchBoardActivityTypeLabel(entry.type)}</Tag>
                    <When title={formatCatchBoardDate(entry.at)}>
                      {formatCatchBoardRelativeTime(entry.at) ||
                        formatCatchBoardDate(entry.at)}
                    </When>
                  </ItemHeader>
                  <Summary title={entry.summary || summary}>{summary}</Summary>
                  <Actor>
                    {formatActivityActor(entry) || 'Unknown'}
                  </Actor>
                </ItemBody>
              </Item>
            )
          })}
        </Timeline>
      )}
    </Section>
  )
}

function formatActivityActor(entry = {}) {
  const name = String(entry.name || '').trim()
  if (name) {
    return name
  }
  return String(entry.email || '').trim()
}

const Section = styled.div`
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
`

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const SectionHint = styled.p`
  margin: 4px 0 10px;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const EmptyHint = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const Timeline = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0 0 0 10px;
  border-left: 2px solid rgba(0, 0, 0, 0.08);
`

const Item = styled.li`
  position: relative;
  padding: 0 0 12px 14px;

  &:last-child {
    padding-bottom: 0;
  }
`

const Dot = styled.span`
  position: absolute;
  left: -6px;
  top: 6px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colours.broccoliGreen || '#2d6a4f'};
  border: 2px solid #fff;
  box-sizing: border-box;
`

const ItemBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const ItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const When = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
  white-space: nowrap;
`

const Summary = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
  overflow-wrap: anywhere;
`

const Actor = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

export default CatchBoardDealActivityTimeline
